import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatDate } from '../utils/api';
import { Calendar, MapPin } from 'lucide-react';

export default function EventCard({ event }) {
  const navigate = useNavigate();
  const capacityPercent = event.capacity ? (event.total_rsvps / event.capacity) * 100 : 0;
  const spotsLeft = event.capacity ? event.spots_available : null;

  return (
    <motion.div
      className="group relative rounded-xl sm:rounded-2xl p-4 sm:p-6 bg-white/60 border border-white/30 shadow-[0_10px_30px_rgba(11,34,64,0.1)] hover:shadow-[0_12px_40px_rgba(11,34,64,0.24)] transition-all duration-300 cursor-pointer"
      whileHover={{ y: -4 }}
      onClick={() => navigate(`/events/${event.slug}`)}
      data-testid="event-card"
    >
      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
        <span className="px-2 sm:px-3 py-1 bg-[#0B2240] text-white text-[10px] sm:text-xs font-semibold rounded-full uppercase tracking-wider">
          {event.type.replace('_', ' ')}
        </span>
        <span className="px-2 sm:px-3 py-1 bg-[#EDEBE6] text-[#0B2240] text-[10px] sm:text-xs rounded-full">
          {event.location_city}
        </span>
      </div>

      {/* Title */}
      <h3 className="font-heading text-xl sm:text-2xl font-semibold text-[#0B2240] mb-2" data-testid="event-card-title">
        {event.title}
      </h3>

      {/* Description */}
      <p className="text-[#4C5A70] text-sm sm:text-base line-clamp-3 mb-3 sm:mb-4 whitespace-pre-line">
        {event.description}
      </p>

      {/* Meta Info */}
      <div className="space-y-2 text-xs sm:text-sm text-[#4C5A70] mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="truncate">
            {new Date(event.start_datetime).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
            {' • '}
            {new Date(event.start_datetime).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              timeZone: event.timezone || 'America/New_York'
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="truncate">{event.location_name}</span>
        </div>
      </div>

      {/* Capacity Indicator */}
      {event.requires_rsvp && event.capacity && (
        <div className="mb-3 sm:mb-4" data-testid="event-card-capacity">
          <div className="flex justify-between text-xs sm:text-sm mb-2">
            <span className="text-[#4C5A70]">{event.total_rsvps} of {event.capacity} spots</span>
            <span className={`font-medium ${spotsLeft <= 5 ? 'text-[#C7362E]' : 'text-[#1FA3A3]'}`}>
              {spotsLeft} left
            </span>
          </div>
          <div className="h-2 bg-[#EDEBE6] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#0B2240] to-[#1FA3A3] rounded-full transition-all duration-500"
              style={{ width: `${Math.min(capacityPercent, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* CTA Button - Responsive */}
      <button
        className="w-full h-10 sm:h-11 text-sm sm:text-base bg-[#0B2240] text-white rounded-[12px] font-medium hover:bg-[#0A1D35] transition-colors focus-visible:ring-2 focus-visible:ring-[#1FA3A3] focus-visible:outline-none"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/events/${event.slug}`);
        }}
        data-testid="event-card-cta"
      >
        {event.requires_rsvp ? (event.is_at_capacity ? 'Join Waitlist' : 'RSVP Now') : 'View Details'}
      </button>
    </motion.div>
  );
}
